/*
  Transformation Stories
  Centred story slider with synced thumbnail navigation. Each story keeps a
  single answer open, and on mobile the in-card nav steps through the answers.
*/

const STORIES_SINGLE_ITEM_QUERY = '(max-width: 989px)';

class TransformationStories {
  constructor(root) {
    this.root = root;
    this.sliderElement = root.querySelector('[data-stories-slider]');
    this.thumbsElement = root.querySelector('[data-stories-thumbs]');
    this.previousButtons = Array.from(root.querySelectorAll('[data-stories-prev]'));
    this.nextButtons = Array.from(root.querySelectorAll('[data-stories-next]'));
    this.items = Array.from(root.querySelectorAll('[data-stories-item]'));

    this.handleResize = this.handleResize.bind(this);
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);

    this.initAccordions();
    this.initItemNavigation();
    this.initSlider();

    window.addEventListener('resize', this.handleResize);
  }

  initAccordions() {
    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-stories-trigger]');

      if (!trigger) return;

      trigger.addEventListener('click', () => this.toggleItem(item));
      this.syncPanelHeight(item);
    });
  }

  /**
   * Builds one navigation group per story so the arrows and dots move between
   * that story's answers.
   */
  initItemNavigation() {
    const accordions = Array.from(this.root.querySelectorAll('[data-stories-accordion]'));

    this.itemGroups = accordions.map((group) => {
      const scope = group.parentElement;
      const nav = scope ? scope.querySelector('[data-stories-item-nav]') : null;

      const state = {
        items: Array.from(group.querySelectorAll('[data-stories-item]')),
        dots: nav ? Array.from(nav.querySelectorAll('[data-stories-item-dot]')) : [],
        listeners: [],
      };

      if (nav) {
        const previous = nav.querySelector('[data-stories-item-prev]');
        const next = nav.querySelector('[data-stories-item-next]');

        if (previous) {
          const handler = () => this.stepItem(state, -1);
          previous.addEventListener('click', handler);
          state.listeners.push({ element: previous, handler });
        }

        if (next) {
          const handler = () => this.stepItem(state, 1);
          next.addEventListener('click', handler);
          state.listeners.push({ element: next, handler });
        }

        state.dots.forEach((dot, index) => {
          const handler = () => this.showItem(state, index);
          dot.addEventListener('click', handler);
          state.listeners.push({ element: dot, handler });
        });
      }

      this.syncDots(state);

      return state;
    });
  }

  /**
   * @param {Object} state - Navigation state for one story.
   * @param {number} direction - Positive moves forward, negative moves back.
   */
  stepItem(state, direction) {
    if (!state.items.length) return;

    const current = state.items.findIndex((item) => item.classList.contains('is-open'));
    const nextIndex = (current + direction + state.items.length) % state.items.length;

    this.showItem(state, nextIndex);
  }

  /**
   * @param {Object} state - Navigation state for one story.
   * @param {number} index - Index of the answer to reveal.
   */
  showItem(state, index) {
    const target = state.items[index];

    if (!target) return;

    state.items.forEach((item) => {
      this.setItemState(item, item === target);
    });

    this.syncDots(state);
  }

  /**
   * @param {Object} state - Navigation state for one story.
   */
  syncDots(state) {
    const current = state.items.findIndex((item) => item.classList.contains('is-open'));

    state.dots.forEach((dot, index) => {
      const isActive = index === current;

      dot.classList.toggle('transformation-stories__dot--active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  /**
   * Opens the given item and closes every other item of the same story. When a
   * single answer is visible, the open item stays open so the card is never empty.
   *
   * @param {HTMLElement} item - The accordion item that was activated.
   */
  toggleItem(item) {
    const group = item.closest('[data-stories-accordion]');
    const wasOpen = item.classList.contains('is-open');

    if (wasOpen && window.matchMedia(STORIES_SINGLE_ITEM_QUERY).matches) return;

    if (group) {
      group.querySelectorAll('[data-stories-item].is-open').forEach((openItem) => {
        if (openItem !== item) {
          this.setItemState(openItem, false);
        }
      });
    }

    this.setItemState(item, !wasOpen);

    const state = (this.itemGroups || []).find((group) => group.items.includes(item));

    if (state) this.syncDots(state);
  }

  /**
   * @param {HTMLElement} item - The accordion item to update.
   * @param {boolean} isOpen - Whether the item should be expanded.
   */
  setItemState(item, isOpen) {
    const trigger = item.querySelector('[data-stories-trigger]');

    item.classList.toggle('is-open', isOpen);

    if (trigger) {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    this.syncPanelHeight(item);
  }

  /**
   * @param {HTMLElement} item - The accordion item whose panel should be measured.
   */
  syncPanelHeight(item) {
    const panel = item.querySelector('[data-stories-panel]');

    if (!panel) return;

    panel.style.maxHeight = item.classList.contains('is-open') ? `${panel.scrollHeight}px` : '0px';
  }

  initSlider() {
    if (!this.sliderElement) return;

    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this.initSlider(), { once: true });
      return;
    }

    if (!this.sliderElement.querySelector('.swiper-slide')) return;

    if (this.thumbsElement) {
      this.thumbsSwiper = new Swiper(this.thumbsElement, {
        slidesPerView: 'auto',
        spaceBetween: 18,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
      });
    }

    const options = {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 12,
      speed: 500,
      watchSlidesProgress: true,
      preventClicks: false,
      loop: true,
      loopFillGroupWithBlank: true,
      preventClicksPropagation: false,
      breakpoints: {
        990: {
          spaceBetween: 24,
        },
      },
      on: {
        init: (swiper) => this.syncArrows(swiper),
        slideChange: (swiper) => this.syncArrows(swiper),
      },
    };

    if (this.thumbsSwiper) {
      options.thumbs = { swiper: this.thumbsSwiper };
    }

    this.swiper = new Swiper(this.sliderElement, options);

    this.previousButtons.forEach((button) => button.addEventListener('click', this.handlePrevious));
    this.nextButtons.forEach((button) => button.addEventListener('click', this.handleNext));
  }

  handlePrevious() {
    if (this.swiper) this.swiper.slidePrev();
  }

  handleNext() {
    if (this.swiper) this.swiper.slideNext();
  }

  /**
   * @param {Object} swiper - The story Swiper instance.
   */
  syncArrows(swiper) {
    this.previousButtons.forEach((button) => {
      button.disabled = swiper.isBeginning;
    });

    this.nextButtons.forEach((button) => {
      button.disabled = swiper.isEnd;
    });
  }

  handleResize() {
    this.items.forEach((item) => {
      if (item.classList.contains('is-open')) {
        this.syncPanelHeight(item);
      }
    });
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);

    this.previousButtons.forEach((button) =>
      button.removeEventListener('click', this.handlePrevious)
    );
    this.nextButtons.forEach((button) => button.removeEventListener('click', this.handleNext));

    (this.itemGroups || []).forEach((state) => {
      state.listeners.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
    });

    this.itemGroups = [];

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
  }
}

/**
 * @param {ParentNode} [scope] - Container to search within, defaults to the document.
 */
function initTransformationStories(scope) {
  const roots = (scope || document).querySelectorAll('[data-transformation-stories]');

  roots.forEach((root) => {
    if (root.dataset.storiesInitialized === 'true') return;

    root.dataset.storiesInitialized = 'true';
    root.transformationStories = new TransformationStories(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTransformationStories());
} else {
  initTransformationStories();
}

document.addEventListener('shopify:section:load', (event) => {
  initTransformationStories(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-transformation-stories]').forEach((root) => {
    if (root.transformationStories) {
      root.transformationStories.destroy();
      root.transformationStories = null;
    }

    root.dataset.storiesInitialized = 'false';
  });
});
